import { useAARData } from "../../context/AARDataContext";
import "./Dashboard.css";

function Dashboard() {
  // ==========================================
  // SHARED AAR DATA
  // ==========================================

  const { projects } = useAARData();

  const totalAARs = projects.length;


  // ==========================================
  // AAR STATUS
  // ==========================================

  const published = projects.filter(
    (project) =>
      String(project.status || "").toUpperCase() === "PUBLISHED"
  ).length;

  const underReview = projects.filter(
    (project) =>
      String(project.status || "").toUpperCase() === "UNDER REVIEW"
  ).length;

  const draft = projects.filter(
    (project) =>
      String(project.status || "").toUpperCase() === "DRAFT"
  ).length;


  // ==========================================
  // ACTION CALCULATIONS
  // ==========================================

  let totalActions = 0;
  let openActions = 0;
  let inProgressActions = 0;
  let completedActions = 0;

  projects.forEach((project) => {
    const actions = Array.isArray(project.actions)
      ? project.actions
      : [];

    totalActions += actions.length;

    actions.forEach((action) => {
      const status = String(
        action.status || ""
      ).toUpperCase();

      if (status === "COMPLETED") {
        completedActions++;
      } else if (status === "IN PROGRESS") {
        inProgressActions++;
      } else {
        openActions++;
      }
    });
  });


  // ==========================================
  // EFFECTIVENESS
  // ==========================================

  const effectiveness =
    totalActions > 0
      ? Math.round(
          (completedActions / totalActions) * 100
        )
      : 0;


  // ==========================================
  // STATUS PERCENTAGES
  // ==========================================

  const publishedPercentage =
    totalAARs > 0
      ? (published / totalAARs) * 100
      : 0;

  const underReviewPercentage =
    totalAARs > 0
      ? (underReview / totalAARs) * 100
      : 0;

  const draftPercentage =
    totalAARs > 0
      ? (draft / totalAARs) * 100
      : 0;


  return (
    <main className="dashboard">

      {/* ==========================================
          HEADER
      ========================================== */}

      <section className="dashboard-header">

        <div className="dashboard-container">

          <div>

            <span className="dashboard-label">
              AFTER ACTION REVIEW
            </span>

            <h1>
              AAR Dashboard
            </h1>

            <p>
              Turning project experience into
              measurable improvement.
            </p>

          </div>

        </div>

      </section>


      {/* ==========================================
          KPI SECTION
      ========================================== */}

      <section className="dashboard-section">

        <div className="dashboard-container">

          <div className="dashboard-kpi-grid">


            {/* TOTAL AARS */}

            <div className="dashboard-kpi">

              <span>
                Total AARs
              </span>

              <strong>
                {totalAARs}
              </strong>

              <small>
                Project records
              </small>

            </div>


            {/* PUBLISHED */}

            <div className="dashboard-kpi">

              <span>
                Published
              </span>

              <strong>
                {published}
              </strong>

              <small>
                Published AARs
              </small>

            </div>


            {/* UNDER REVIEW */}

            <div className="dashboard-kpi">

              <span>
                Under Review
              </span>

              <strong>
                {underReview}
              </strong>

              <small>
                Awaiting review
              </small>

            </div>


            {/* OPEN ACTIONS */}

            <div className="dashboard-kpi">

              <span>
                Open Actions
              </span>

              <strong>
                {openActions}
              </strong>

              <small>
                Require attention
              </small>

            </div>


            {/* EFFECTIVENESS */}

            <div className="dashboard-kpi">

              <span>
                Effectiveness
              </span>

              <strong>
                {effectiveness}%
              </strong>

              <small>
                Actions completed
              </small>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          ANALYTICS
      ========================================== */}

      <section className="dashboard-section">

        <div className="dashboard-container">

          <div className="dashboard-columns">


            {/* ======================================
                AAR STATUS
            ====================================== */}

            <div className="dashboard-card">

              <span className="card-label">
                AAR ANALYTICS
              </span>

              <h2>
                AAR Status
              </h2>


              <div className="dashboard-status">


                {/* PUBLISHED */}

                <div className="status-item">

                  <div>

                    <span>
                      Published
                    </span>

                    <strong>
                      {published}
                    </strong>

                  </div>


                  <div className="status-track">

                    <div
                      style={{
                        width: `${publishedPercentage}%`,
                      }}
                    />

                  </div>

                </div>


                {/* UNDER REVIEW */}

                <div className="status-item">

                  <div>

                    <span>
                      Under Review
                    </span>

                    <strong>
                      {underReview}
                    </strong>

                  </div>


                  <div className="status-track">

                    <div
                      style={{
                        width: `${underReviewPercentage}%`,
                      }}
                    />

                  </div>

                </div>


                {/* DRAFT */}

                <div className="status-item">

                  <div>

                    <span>
                      Draft
                    </span>

                    <strong>
                      {draft}
                    </strong>

                  </div>


                  <div className="status-track">

                    <div
                      style={{
                        width: `${draftPercentage}%`,
                      }}
                    />

                  </div>

                </div>


              </div>

            </div>


            {/* ======================================
                ACTION SUMMARY
            ====================================== */}

            <div className="dashboard-card">

              <span className="card-label">
                ACTION MANAGEMENT
              </span>

              <h2>
                Action Summary
              </h2>


              <div className="action-summary">


                {/* TOTAL */}

                <div>

                  <strong>
                    {totalActions}
                  </strong>

                  <span>
                    Total Actions
                  </span>

                </div>


                {/* OPEN */}

                <div>

                  <strong>
                    {openActions}
                  </strong>

                  <span>
                    Open
                  </span>

                </div>


                {/* IN PROGRESS */}

                <div>

                  <strong>
                    {inProgressActions}
                  </strong>

                  <span>
                    In Progress
                  </span>

                </div>


                {/* COMPLETED */}

                <div>

                  <strong>
                    {completedActions}
                  </strong>

                  <span>
                    Completed
                  </span>

                </div>


              </div>


              {/* COMPLETION RATE */}

              <div className="effectiveness">

                <div className="effectiveness-header">

                  <span>
                    Completion Rate
                  </span>

                  <strong>
                    {effectiveness}%
                  </strong>

                </div>


                <div className="effectiveness-track">

                  <div
                    style={{
                      width: `${effectiveness}%`,
                    }}
                  />

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          RECENT AARS
      ========================================== */}

      <section className="dashboard-section">

        <div className="dashboard-container">

          <div className="dashboard-card recent-aar-card">


            <div className="recent-aar-header">

              <div>

                <span className="card-label">
                  PROJECT LIBRARY
                </span>

                <h2>
                  Recent AAR Records
                </h2>

              </div>


              <span className="recent-count">
                {projects.length} Records
              </span>

            </div>


            <div className="recent-aar-list">

              {projects.slice(0, 5).map(
                (project) => (

                  <div
                    className="recent-aar-item"
                    key={project.id}
                  >


                    <div className="recent-aar-info">

                      <span className="recent-aar-type">
                        {project.type || "PROJECT"}
                      </span>

                      <h3>
                        {project.title ||
                          "Untitled Project"}
                      </h3>

                      <p>
                        {project.subtitle ||
                          "No description available"}
                      </p>

                    </div>


                    <div className="recent-aar-meta">

                      <span
                        className={`dashboard-status-badge ${
                          String(
                            project.status || ""
                          )
                            .toLowerCase()
                            .replace(
                              /\s+/g,
                              "-"
                            )
                        }`}
                      >
                        {project.status ||
                          "UNKNOWN"}
                      </span>


                      <span className="recent-aar-date">
                        {project.date ||
                          "No date"}
                      </span>

                    </div>


                  </div>

                )
              )}

            </div>

          </div>

        </div>

      </section>


      {/* ==========================================
          FOOTER MESSAGE
      ========================================== */}

      <section className="dashboard-bottom">

        <div className="dashboard-container">

          <h2>
            Learn. Improve. Perform Better.
          </h2>

          <p>
            Use lessons from completed projects to
            strengthen future operations.
          </p>

        </div>

      </section>

    </main>
  );
}

export default Dashboard;